import React from 'react';

import I18n from '@module/i18n';

import Transition from './transition';

// So, this might not be the best way to do this - but
// it does work. So `Dropzone` is both a static class
// and React component in one.
export default class Dropzone extends React.Component {
  static mounted = false;
  static target = false;

  // The state decides whether or not the dropzone is really visible
  state = {
    visible: false,
    animated: false,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.onDragOver = this.onDragOver.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onDrop = this.onDrop.bind(this);
  }

  // Set the current uploader target, this will enable the
  // Dropzone so the user can easily drop the files.
  static setTarget(target, uploader) {
    if (!target || typeof target.addFiles !== 'function') {
      throw 'InvalidDropTarget';
    }

    Dropzone.target = target;

    if (!uploader) {
      return;
    }
    // Also enable dropping files on our uploader component
    uploader.addEventListener('drop', this.mounted.onDrop, false);
  }

  // Remove the current uploader target, this will disable
  // the Dropzone component from working.
  static removeTarget(uploader) {
    Dropzone.target = null;

    // Also disable the drop event on our uploader component
    if (!uploader) {
      return;
    }
    uploader.removeEventListener('drop', this.mounted.onDrop, false);
  }

  // Try to show the dropzone
  static show() {
    if (!Dropzone.mounted) {
      return;
    }
    if (!Dropzone.target) {
      return false;
    }
    Dropzone.mounted.show();
  }

  // Try to hide the dropzone
  static hide() {
    if (!Dropzone.mounted) {
      return;
    }
    Dropzone.mounted.hide();
  }

  // Check if the drag event is supported by Dropzone, basically
  // we just have 1 place where we make sure the drag/drop cmd
  // actually contains files.
  static availableForEvent(e) {
    if (!Dropzone.target) {
      return false;
    }
    if (!e || !e.dataTransfer) {
      return false;
    }
    if (!e.dataTransfer.types) {
      return false;
    }

    try {
      // .types is not always an Array so does not always have .indexOf support
      const types = Array.prototype.slice.call(e.dataTransfer.types);
      return types.indexOf('Files') !== -1;
    } catch (e) {
      return false;
    }
  }

  // We are using the dragover event to determine whether someone is
  // dragging files in our window. In most cases the dragging will
  // actually be happening in the wallpaper iframe though.
  onDragOver(e) {
    e.preventDefault();
    if (!Dropzone.availableForEvent(e)) {
      return;
    }
    Dropzone.show();
  }

  // Leaving the dropzone area with our draggable object, so hide it again
  onDragLeave(e) {
    e.preventDefault();
    Dropzone.hide();
  }

  // When dropping a file we'll first want to make sure it contains
  // actual files otherwise it's not for us to handle.
  onDrop(e) {
    // If this is a supported event and we have an uploader, let's DO THIS!
    if (Dropzone.target) {
      Dropzone.target.addFiles(e.dataTransfer.files);
    }

    e.preventDefault();
    Dropzone.hide();
  }

  // When the component mounts we need to do some things
  componentDidMount() {
    if (!this.node) {
      return;
    }
    // Store the reference to this component
    Dropzone.mounted = this;

    // Set the current state (visually)
    this.componentDidUpdate({}, {});

    // Bind our events
    document.body.addEventListener('dragover', this.onDragOver, false);
    this.node.addEventListener('dragleave', this.onDragLeave, false);
    this.node.addEventListener('drop', this.onDrop, false);
  }

  // Show the dropzone
  show() {
    this.setState({ visible: true, animated: false });
  }

  // Hide the dropzone
  hide(animated = false) {
    this.setState({ visible: false, animated });
  }

  // When a new state is set we might not need to re-render,
  // given the state can be identical to the current one.
  shouldComponentUpdate(nextProps, nextState) {
    return nextState.visible !== this.state.visible;
  }

  // When the state changes we might need to animate it
  // ourselves. But we can do this here, easy!
  componentDidUpdate(prevProps, prevState) {
    new Transition({
      component: this.node,
      dropAnimation: this.state.animated,
      prevState: prevState.visible,
      newState: this.state.visible,
    });
  }

  // Cleanup the component
  componentWillUnmount() {
    if (!this.node) {
      return;
    }

    Dropzone.mounted = false;

    // Remove global event listener
    document.body.removeEventListener('dragover', this.onDragOver, false);

    // Stop any running animations
    Transition.cleanup(this.node);

    // Remove the dropzone event listeners too
    this.node.removeEventListener('dragleave', this.onDragLeave, false);
    this.node.removeEventListener('drop', this.onDrop, false);
  }

  // This is a simple representation of our dropzone
  render() {
    return (
      <div ref={(node) => (this.node = node)} className="dropzone">
        <h3 className="dropzone__title">{I18n.t('dropzone.title')}</h3>
        <p className="dropzone__text">{I18n.t('dropzone.text')}</p>
      </div>
    );
  }
}
